<template>
  <div
    v-if="embedStatus === 'loading'"
    class="consent-embed-placeholder"
    :style="{
      backgroundImage: `url('${youtubeImageUrl}')`,
    }"
  ></div>
  <div
    v-else-if="embedStatus === 'disabled'"
    class="consent-embed-placeholder"
    :style="{
      backgroundImage: `linear-gradient(rgba(76, 72, 72, 0.75), rgba(76, 72, 72, 0.75)), url('${youtubeImageUrl}')`,
    }"
  >
    <a
      class="cky-banner-element embed-placeholder-link"
      role="button"
      tabindex="0"
      @click="handleConsentEdgeCases"
    >
      <div class="embed-placeholder-title">
        <h2 class="heading -medium">YouTube Privacy</h2>
      </div>
      <div class="embed-placeholder-body">
        <p>
          YouTube (owned by Google) requires cookies to be enabled. Please open
          <strong class="embed-underline">Cookie Preferences</strong> and enable
          <strong>Functional</strong> cookies to view the video.
        </p>
      </div>
      <div class="embed-placeholder-actions">
        <div class="action">
          <div class="btn -dark -no-case">Open Cookie Preferences</div>
        </div>
      </div>
    </a>
  </div>
  <iframe
    v-else-if="embedStatus === 'enabled'"
    :id="ytEmbedId"
    :width="videoEmbedFormat === 'portrait' ? '765' : '700'"
    :height="videoEmbedFormat === 'portrait' ? '1360' : '394'"
    :src="`https://www.youtube.com/embed/${youtubeId}?rel=0&origin=${origin}&autoplay=0&enablejsapi=1`"
    title="YouTube video player"
    frameborder="0"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowfullscreen
  ></iframe>
</template>

<script setup lang="ts">
/**
 * Youtube embeds that respect cookieyes cookie consnet
 *
 * - check pinia consent status before loading
 * - if functional cookies enabled, show embed
 * - if disabled, show overlay and send analytics event
 */

import { computed, onMounted, ref, watch } from 'vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { useToursStore } from '@/stores/tours'
import { trackEvent } from '@/utils/analytics'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    videoEmbedFormat?: 'landscape' | 'portrait'
    youtubeId: string
    origin: string
  }>(),
  {
    videoEmbedFormat: 'landscape',
  },
)

/** STATE **/
const embedStatus = ref('loading')
const tracked = ref(false)
const ytIframeApiLoaded = ref(false)
const userAuthStore = useUserAuthStore()
const modalStore = useModalsStore()
const { loadTour } = useToursStore()

/** COMPUTED **/
const youtubeImageUrl = computed(
  () => `https://img.youtube.com/vi/${props.youtubeId}/maxresdefault.jpg`,
)

const ytEmbedId = computed(() => `yt-embed-${props.youtubeId}`)

/** WATCHERS **/
watch(
  () => userAuthStore.consents.STORAGE_FUNCTIONALITY,
  (newConsent, prevConsent) => {
    if (newConsent !== prevConsent) {
      refreshEmbedStatus()

      // track consent changed
      if (newConsent) {
        trackEvent({
          category: 'YouTube Embed',
          action: 'Re-enabled',
        })
      }
    }
  },
)

/** LIFECYCLE **/
onMounted(() => {
  // wait for cookiebanner to settle
  setTimeout(() => refreshEmbedStatus(), 50)
})

/** METHODS **/
function refreshEmbedStatus() {
  if (!userAuthStore.consents.STORAGE_FUNCTIONALITY) {
    embedStatus.value = 'disabled'

    // only fire once
    if (!tracked.value) {
      trackEvent({
        category: 'YouTube Embed',
        action: 'Disabled',
      })
      tracked.value = true
    }

    return
  }

  // only load once
  if (!ytIframeApiLoaded.value) {
    ytIframeApiLoaded.value = true
    ytSignInChecker()
  }

  embedStatus.value = 'enabled'
}

/**
 * Some US users may have consent blocking extensions, so check for that
 * and auto accept all.
 *
 * Not really a fix as they will probably have to do this
 * every single time.
 *
 * @see https://www.cookieyes.com/documentation/retrieving-consent-data-using-api-getckyconsent/
 * @see https://www.cookieyes.com/documentation/consent-banner-action-api/
 */
async function handleConsentEdgeCases() {
  // do nothing if missing consent function
  if (!window || !window.getCkyConsent || !window.performBannerAction) {
    return
  }

  const { getCkyConsent, performBannerAction } = window
  const currentConsents = getCkyConsent()

  // US users who somehow do not have functional cookies enabled, they will have
  // to do this every single time
  if (
    !currentConsents.activeLaw &&
    (!currentConsents.categories.functional || !userAuthStore.consents.STORAGE_FUNCTIONALITY)
  ) {
    console.warn('handleConsentEdgeCases - empty activeLaw and missing functionality, auto accept')
    performBannerAction('accept_all')
  }
}

/**
 * Some users (most only VPN) see "Sign in to confirm you're not a bot" error screen.
 *
 * Through testing, we found that the YT iframe player API will throw an error event
 * that we can listen to. This error code is 150.
 *
 * @see https://developers.google.com/youtube/iframe_api_reference
 */
function ytSignInChecker() {
  // async load YT iframe API via script tag
  const tag = document.createElement('script')
  tag.id = 'yt-sign-in-checker'
  tag.src = 'https://www.youtube.com/iframe_api'

  const firstScriptTag = document.getElementsByTagName('script')[0]

  if (!firstScriptTag || !firstScriptTag.parentNode) {
    return
  }

  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

  // YT iframe API requires this to be defined to register events
  window.onYouTubeIframeAPIReady = function () {
    new YT.Player(ytEmbedId.value, {
      events: {
        // 'onReady': onPlayerReady,
        // 'onStateChange': onPlayerStateChange,
        onError: onPlayerError,
      },
    })
  }
}

/**
 * Handle YT error 150 (sign in) and show a guided tour to free users and a modal to anon users.
 *
 * @see https://developers.google.com/youtube/iframe_api_reference
 */
function onPlayerError(event: { data: number }) {
  // console.error('onPlayerError', event)

  if (event.data === 150) {
    // free user, show guided tour
    if (userAuthStore.user.isLoggedIn) {
      loadTour({ tourId: 'ytEmbedSignIn', force: true })
      return
    }

    // anon user, just show modal for FAQ
    modalStore.openSimpleModal({
      type: 'CONFIRM',
      id: `yt-embed-sign-in-modal`,
      props: {
        heading: 'YouTube Player Error',
        subheading: `If the YouTube player embed shows <strong>Sign in to confirm you're not a bot</strong>, then this is an error from YouTube directly, not from fitnessblender.com.  Please see the <a href="/page/faq#video-signin">FAQs for troubleshooting options</a>.`,
        url: '/page/faq#video-signin',
        buttonText: 'View FAQs',
        cancelText: 'Close',
      },
    })
    return
  }
}
</script>
